import { getUmbrellaEligibility } from '@ourbranch/policy-utils';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { Form, useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';
import MUITooltip from '@material-ui/core/Tooltip';
import { isAfter, isSameDay } from 'date-fns';
import {
  lookupsJson,
  offerExcessUM,
  getOptions,
  policyType,
  noUmbrellaStates,
  umbrellaStartDateStates,
  legacyUmbrellaCriteriaStates
} from '@ourbranch/lookups';

import { NotificationCard } from 'core/components/notification-card';
import useSession from 'core/hooks/use-session';
import Field from 'core/components/form/form.v2';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { FieldWithTooltip } from 'core/components/field-with-tooltip';
import Section from 'core/components/section';
import { useToast } from 'core/components/toast';
import { awsDateToDateObject, cleanObject } from 'core/helpers/formatters';
import { selectOptions } from 'core/helpers/select';
import {
  canAddUmbrellaUMUIM,
  canAddUmbrella,
  isUmbrellaBlockedByPriorCarrier,
  getUmbrellaBIMinimum,
  getQualifyingBIPDOption,
  getUmbrellaEstimateMultiPolicyDiscount,
  getUmbrellaLimitBIOccurrence
} from 'core/helpers/quoter.service';
import { useStore } from 'core/store';
import checkCircleIcon from 'core/assets/svg/ic-check-circle-green50.svg';
import exclamationCircleIcon from 'core/assets/svg/ic-exclamation-circle-red50.svg';
import useStyles from './umbrella.styles';
import PropertiesForm from './properties-form';
import BoatsForm from './boats-form';
import CollapsibleSection from 'core/components/collapsible-section';

const id = 'umbrellaCoverage';
const mpdCodesUmbrellaMap = {
  // removing umbrella coverage
  ARU: 'AR',
  ARUS: 'ARS',
  AU: 'A',
  AUS: 'AS',
  HRU: 'HR',
  HU: 'H',
  CRU: 'CR',
  CU: 'C',
  // adding umbrella coverage
  C: 'CU',
  CR: 'CRU',
  AR: 'ARU',
  ARS: 'ARUS',
  A: 'AU',
  AS: 'AUS',
  HR: 'HRU',
  H: 'HU'
};

export const formatUmbrellaValues = (values, includeUmbrellaCoverage = true) => {
  // If values is null/undefined or umbrella coverage is not included, return null
  if (!values || !includeUmbrellaCoverage) {
    return null;
  }

  /**
   * typeof item === 'object' below is a temporary fix until the use case is properly handled
   * @TODO: Support free input addresses for rental properties
   */
  const otherPropertyAddresses = (values.otherPropertyAddresses || []).filter(
    (item) => !!item && typeof item === 'object'
  );
  const rentalPropertyAddresses = (values.rentalPropertyAddresses || []).filter(
    (item) => !!item && typeof item === 'object'
  );
  const watercraftHullLengths = (values.watercraftHullLengths || []).filter((item) => !!item);
  return {
    ...values,
    watercraftHullLengths,
    rentalPropertyAddresses,
    numRentalProperties: rentalPropertyAddresses.length,
    otherPropertyAddresses,
    numOtherProperties: otherPropertyAddresses.length
  };
};

const umbrellaTooltips = {
  uninsuredMotoristLimit:
    'This provides excess coverage in the event underlying UM/UIM BI limits are not enough to cover bodily injury damages caused by someone who has no (or not enough) insurance.',
  numLicensedDriversUnder25:
    'This number should include all licensed drivers under age 25 living in the household, even if they are not covered by the Branch auto policy.',
  numVehiclesTotal:
    'This number should include all of the cars belonging to this household, even if they are not covered by the Branch auto policy.',
  numPersonalWatercraft:
    "Personal watercraft, such as jet skis and water scooters, are propelled by a water jet pump engine. They're operated by a person or persons sitting, standing or kneeling on them. These crafts are smaller than boats, usually under 16 feet long. Unlike boats, we don't need to know their hull length."
};

const mockUmbrellaValues = {
  watercraftHullLengths: [],
  rentalPropertyAddresses: [],
  otherPropertyAddresses: [],
  numRVs: 0,
  numMotorcyclesScooters: 0,
  numATVs: 0,
  numPersonalWatercraft: 0,
  liabilityCoverageLimit: null,
  uninsuredMotoristLimit: null,
  numVehiclesTotal: null,
  numLicensedDriversUnder25: null
};

const Umbrella = ({ fromPolicy, disabled, coverageX, state }) => {
  const classes = useStyles();
  const { values, initialValues, setFieldValue, setFieldTouched, errors, touched } = useFormikContext();

  const { umbrellaCoverage, includeUmbrella, autoCoverage, drivers } = values;

  const [cleaned, setCleaned] = useState(false);
  const [canOfferUmbrella, setCanOfferUmbrella] = useState(false);
  const [canAddUM, setCanAddUM] = useState(false);
  const {
    offer: offerStore,
    account: {
      policies: policiesStore,
      policies: { policy: policyStore }
    }
  } = useStore();

  const { quote } = offerStore?.offer || {};

  const { policyLimitUMBI, policyLimitUIMBI, policyLimitBIPD } = autoCoverage || {};

  const { isAgency } = useSession();

  // for all of the below, if autoCoverage doesn't exist, this is a policy, and we don't
  // re-validate on policies for umbrella eligibility based upon auto.
  // in a Bix offer or state Bix policy and has 500 umbi and uimbi;

  // Bundle-to-existing offers (HB) have no autoCoverage/drivers of their own — those live
  // on the linked customer's in-force auto policy. Policies deliberately skip this fallback
  // (see comment above: we don't re-validate umbrella on policies based upon auto).
  const linkedInputs = !fromPolicy ? offerStore?.linkedPolicyUmbrellaInputs : null;

  // overrides (allowUmbrella, allow300KCSL) live on the policy record's renewalVariables;
  // offers have none. Without this the toggle stays disabled even when an override is set,
  // while the save-time validation (which does receive renewalVariables) would pass. #33765
  const renewalVariables = fromPolicy ? policyStore.policy?.renewalVariables : undefined;
  const allDrivers = [...(drivers || quote?.drivers || []), ...(linkedInputs?.drivers || [])];

  useEffect(() => {
    setCanAddUM(
      canAddUmbrellaUMUIM({
        policyLimitUMBI: policyLimitUMBI ?? linkedInputs?.policyLimitUMBI,
        policyLimitUIMBI: policyLimitUIMBI ?? linkedInputs?.policyLimitUIMBI,
        state
      }) && offerExcessUM[state]
    );
    // on policies autoCoverage can come from the most recent INACTIVE auto, so adding
    // umbrella additionally requires an active/future auto policy (mirrors the Yup schema)
    const hasAutoPolicyForUmbrella =
      !fromPolicy || !!policiesStore.hasActiveOrFutureAutoPolicy || !!renewalVariables?.allowUmbrella;
    setCanOfferUmbrella(
      hasAutoPolicyForUmbrella &&
        canAddUmbrella({
          policyLimitBIPD: policyLimitBIPD ?? linkedInputs?.policyLimitBIPD,
          coverageX,
          drivers: allDrivers,
          global: fromPolicy ? policyStore.policyDetails.global : quote?.global,
          renewalVariables,
          includeUmbrella: fromPolicy ? policyStore.policyDetails.includeUmbrella : false
        })
    );
  }, [
    policyLimitUMBI,
    policyLimitUIMBI,
    state,
    fromPolicy,
    quote,
    policyStore,
    policiesStore,
    drivers,
    policyLimitBIPD,
    coverageX,
    linkedInputs,
    renewalVariables
  ]);

  const blockedByPriorCarrier = isUmbrellaBlockedByPriorCarrier({
    global: fromPolicy ? policyStore.policyDetails?.global : quote?.global,
    renewalVariables,
    includeUmbrella: fromPolicy ? policyStore.policyDetails?.includeUmbrella : false
  });

  const toast = useToast();
  const { id: accountId } = useParams();
  const { requirements } = getUmbrellaEligibility({
    state,
    uninsuredMotoristLimit: umbrellaCoverage?.uninsuredMotoristLimit,
    policyLimitUMBI: policyLimitUMBI ?? linkedInputs?.policyLimitUMBI,
    policyLimitUIMBI: policyLimitUIMBI ?? linkedInputs?.policyLimitUIMBI,
    policyLimitBIPD: policyLimitBIPD ?? linkedInputs?.policyLimitBIPD,
    coverageX,
    drivers: allDrivers,
    // so the checklist honors the same allow300KCSL override canAddUmbrella uses
    renewalVariables
  });
  const umbrellaBIMinimum = getUmbrellaBIMinimum(renewalVariables);
  const requirementsMet = {
    ...requirements,
    activeAutoPolicy: fromPolicy
      ? !!renewalVariables?.allowUmbrella || !!policiesStore.hasActiveOrFutureAutoPolicy
      : requirements.activeAutoPolicy
  };
  const umbrellaReadyAfter = (fixedKey) =>
    Object.entries(requirementsMet).every(([key, met]) => key === fixedKey || met) ? ' You may now add umbrella.' : '';

  const isHomeLiability = fromPolicy ? policyStore.policy?.policyType === policyType.Home : !!values.homeCoverage;
  let liabilityField = null;
  if (isHomeLiability) {
    liabilityField = 'homeCoverage.coverageX';
  } else if (values.condoCoverage) {
    liabilityField = 'condoCoverage.coverageX';
  }
  const fixHomeownersLiability = liabilityField
    ? () => {
        setFieldValue(liabilityField, 300000);
        setFieldTouched(liabilityField, true, false);
        toast.notify({
          type: 'success',
          message: `Homeowner’s liability limit has been updated to $300,000.${umbrellaReadyAfter('homeownersLiability')}`
        });
      }
    : undefined;

  const qualifyingBIPD = getQualifyingBIPDOption(
    getOptions('policyLimitBIPD', state),
    values.autoCoverage?.policyLimitBIPD,
    renewalVariables
  );
  const activeAutoPolicy = fromPolicy ? policiesStore.hasActiveOrFutureAutoPolicy : null;
  let fixAutoBILimit;
  if (!fromPolicy && values.autoCoverage && qualifyingBIPD) {
    fixAutoBILimit = () => {
      setFieldValue('autoCoverage.policyLimitBIPD', qualifyingBIPD.id);
      setFieldTouched('autoCoverage.policyLimitBIPD', true, false);
      toast.notify({
        type: 'success',
        message: `Auto BI limit has been updated to ${qualifyingBIPD.value ?? qualifyingBIPD.id}.${umbrellaReadyAfter('autoBILimit')}`
      });
    };
  } else if (fromPolicy && activeAutoPolicy) {
    fixAutoBILimit = () => {
      window.open(`/customer/${accountId}/policy/${activeAutoPolicy.id}`, '_blank');
      toast.notify({
        type: 'success',
        message: 'Applying this requires an endorsement on the auto policy. We\u2019ve opened it in a new tab for you.'
      });
    };
  }

  const requirementRows = [
    {
      key: 'activeAutoPolicy',
      label: 'Customer must have an active auto policy',
      requirementMet: requirementsMet.activeAutoPolicy
    },
    {
      key: 'autoBILimit',
      label:
        umbrellaBIMinimum === 500
          ? 'Auto BI needs to be at least $500K per occurrence ($250K/$500K for split)'
          : `Auto BI needs to be at least $${umbrellaBIMinimum}K per occurrence (override applied to this policy)`,
      requirementMet: requirements.autoBILimit,
      onApply: fromPolicy || requirements.activeAutoPolicy ? fixAutoBILimit : undefined,
      applyButtonText: fromPolicy ? 'Go to auto policy' : undefined,
      applyTooltip: fromPolicy ? 'Opens the auto policy in a new tab where the BI limit can be updated.' : undefined
    },
    {
      key: 'homeownersLiability',
      label: 'Homeowner’s liability needs to be at least $300K',
      requirementMet: requirements.homeownersLiability,
      onApply: fixHomeownersLiability
    },
    {
      key: 'autoUMUIM',
      label: 'Auto uninsured and underinsured motorist limits must qualify for excess coverage',
      requirementMet: requirements.autoUMUIM
    },
    {
      key: 'stateAllowsExcessUM',
      label: 'Excess uninsured and underinsured motorist coverage must be available in this state',
      requirementMet: requirements.stateAllowsExcessUM
    },
    {
      key: 'noExcludedDrivers',
      label: 'There cannot be any excluded drivers',
      requirementMet: requirements.noExcludedDrivers
    },
    {
      key: 'noMajorViolationOrUDR',
      label: 'There cannot be drivers with a major violation or unverifiable driving record in the last 3 years',
      requirementMet: requirements.noMajorViolationOrUDR,
      blockingDrivers: allDrivers.filter((d) => d.hasMajorViolationBlockingUmbrella ?? d.hasViolationsBlockingUmbrella)
    },
    !legacyUmbrellaCriteriaStates.includes(state) && {
      key: 'noAtFaultAccidentBlockingUmbrella',
      label:
        'Drivers 21 and under or 70 and over cannot have an at-fault accident in the last 3 years, and no driver can have an at-fault accident with $50K or more paid for bodily injury in the last 5 years',
      requirementMet: requirements.noAtFaultAccidentBlockingUmbrella,
      blockingDrivers: allDrivers.filter((d) => d.hasAtFaultAccidentBlockingUmbrella)
    }
  ].filter(Boolean);

  // #34681: lets staff price umbrella when the home qualifies but the auto doesn't yet. The preview this
  // produces is estimate-only, and policy-mod refuses to confirm it.
  const autoRequirementKeys = [
    'activeAutoPolicy',
    'autoBILimit',
    'noExcludedDrivers',
    'noMajorViolationOrUDR',
    'noAtFaultAccidentBlockingUmbrella',
    'autoUMUIM'
  ];
  const canEstimateUmbrella =
    fromPolicy &&
    (!includeUmbrella || !requirements.autoUMUIM) &&
    (!canOfferUmbrella || !requirements.autoUMUIM) &&
    !blockedByPriorCarrier &&
    requirementsMet.homeownersLiability &&
    requirementsMet.stateAllowsExcessUM &&
    autoRequirementKeys.some((key) => !requirementsMet[key]);
  const startUmbrellaEstimate = () => {
    setFieldValue('estimateOnly', true);
    // price at the umbrella tier the auto policy would have at the lowest qualifying BI
    setFieldValue(
      id,
      {
        ...mockUmbrellaValues,
        ...(includeUmbrella ? umbrellaCoverage : {}),
        limitBIOccurrence: getUmbrellaLimitBIOccurrence(qualifyingBIPD?.id)
      },
      false
    );
    setFieldTouched('multiPolicyDiscount', true);
    setFieldValue('multiPolicyDiscount', getUmbrellaEstimateMultiPolicyDiscount(values.multiPolicyDiscount));
    setFieldValue('includeUmbrella', true);
  };

  const validUMUIMOptions = getOptions('uninsuredMotoristLimit', state);

  // always show umbrella in policy (but will be disabled if not correct auto coverages)
  // only show umbrella in offer if it's a Home with Bundle Discount or a Home/Condo and Auto Bundle
  const offerOptionsAllowingUmbrella = ['HB', 'HA', 'CA'];
  let umbrellaInThisState = !noUmbrellaStates[state];
  if (umbrellaStartDateStates[state]) {
    const rateControlDate = fromPolicy
      ? new Date()
      : awsDateToDateObject(offerStore?.offer?.quote?.global.rateControlDate);
    const stateDate = new Date(umbrellaStartDateStates[state]);
    umbrellaInThisState = isAfter(rateControlDate, stateDate) || isSameDay(rateControlDate, stateDate);
  }
  const showUmbrellaSection =
    umbrellaInThisState &&
    (fromPolicy ||
      (offerOptionsAllowingUmbrella.includes(offerStore?.selectedOption) && quote?.offerings.offerUmbrella));

  const umbrellaOpts = lookupsJson.umbrellaLimit.filter((d) => {
    if (fromPolicy && policyStore.policyDetails?.umbrellaCoverage?.liabilityCoverageLimit === 2000000) {
      return d.id <= 2000000;
    }
    return d.id <= 1000000;
  });

  useEffect(() => {
    // if we add umbrella we add the defaults to the form
    if (includeUmbrella) {
      if (!umbrellaCoverage) {
        setCleaned(false);
        setFieldValue(
          id,
          {
            ...mockUmbrellaValues
          },
          false
        );
      } else if (!cleaned) {
        setCleaned(true);
        setFieldValue(id, { ...mockUmbrellaValues, ...cleanObject({ ...umbrellaCoverage }) }, false);
      }
    } else {
      // When umbrella is turned off, set to null
      setFieldValue(id, null, false);
      setCleaned(false);
    }
  }, [includeUmbrella, setFieldValue, umbrellaCoverage, setCleaned, cleaned]);

  return (
    showUmbrellaSection && (
      <Section title="Umbrella Coverage" type="SubSection" id="includeUmbrella">
        <div className={classes.containerDark}>
          <Form disabled={disabled}>
            <Grid container spacing={4} alignItems="flex-start">
              <Field
                id="includeUmbrella"
                name="includeUmbrella"
                type="switch"
                label="Umbrella Coverage"
                mode="dark"
                className={classes.toggle}
                xs={canEstimateUmbrella ? 6 : 12}
                onChange={() => {
                  if (values.estimateOnly) {
                    setFieldValue('estimateOnly', false);
                    setFieldValue('multiPolicyDiscount', initialValues.multiPolicyDiscount);
                    return;
                  }
                  if (values.multiPolicyDiscount && mpdCodesUmbrellaMap[values.multiPolicyDiscount]) {
                    setFieldTouched('multiPolicyDiscount', true);
                    setFieldValue('multiPolicyDiscount', mpdCodesUmbrellaMap[values.multiPolicyDiscount]);
                  }
                }}
                disabled={!canOfferUmbrella && !values.includeUmbrella && !errors.includeUmbrella}
                fast={false}
              />
              {canEstimateUmbrella && (
                <Grid item xs={6} className={classes.estimateAction}>
                  <MUITooltip
                    title="Preview the umbrella price before the auto policy qualifies. You can’t save this change until it does."
                    placement="top"
                    arrow
                  >
                    <Button color="secondary" variant="text" onClick={startUmbrellaEstimate} disabled={disabled}>
                      Estimate umbrella price
                    </Button>
                  </MUITooltip>
                </Grid>
              )}
              {includeUmbrella && values.estimateOnly && (
                <NotificationCard
                  className={classes.requirementsNotification}
                  iconClassName={classes.requirementsIcon}
                  type="infoWhite"
                  noBackground
                  iconSize="icon16"
                >
                  <div className={classes.requirementsContent}>
                    <Label className={classes.requirementsText}>
                      <strong>This is a price estimate.</strong> Preview the change to see the umbrella price. You can’t
                      save it until the auto policy meets the umbrella requirements.
                    </Label>
                  </div>
                </NotificationCard>
              )}
              {fromPolicy && includeUmbrella && !policiesStore.hasActiveOrFutureAutoPolicy && (
                <NotificationCard
                  className={classes.requirementsNotification}
                  iconClassName={classes.requirementsIcon}
                  type="infoWhite"
                  noBackground
                  iconSize="icon16"
                >
                  <div className={classes.requirementsContent}>
                    <Label className={classes.requirementsText}>
                      <strong>
                        If you remove this umbrella coverage and save changes, you won&rsquo;t be able to add it back
                        because there is no longer a Branch auto policy.
                      </strong>{' '}
                      You are still able to adjust the umbrella coverage.
                    </Label>
                  </div>
                </NotificationCard>
              )}
              {((!canOfferUmbrella && !includeUmbrella) || (includeUmbrella && !requirements.autoUMUIM)) &&
                (!errors.includeUmbrella || !touched.includeUmbrella) && (
                  <NotificationCard
                    className={classes.requirementsNotification}
                    iconClassName={classes.requirementsIcon}
                    type="infoWhite"
                    noBackground
                    iconSize="icon16"
                  >
                    <div className={classes.requirementsContent}>
                      {blockedByPriorCarrier ? (
                        <Label className={classes.requirementsText}>
                          <strong>This applicant or member is not eligible to add umbrella.</strong> Unfortunately this
                          applicant or member does not meet Branch&rsquo;s underwriting guidelines to add umbrella.
                        </Label>
                      ) : (
                        <>
                          <Label className={classes.requirementsText}>
                            <strong>This applicant or member does not meet requirements to add umbrella.</strong> You
                            may enable the ability to add umbrella by making changes to meet these requirements.
                          </Label>
                          <Label className={classes.requirementsHeading}>Umbrella requirements:</Label>
                          <div className={classes.requirementsList}>
                            {requirementRows
                              .filter(
                                (row) =>
                                  !['autoUMUIM', 'stateAllowsExcessUM'].includes(row.key) ||
                                  (!!umbrellaCoverage?.uninsuredMotoristLimit &&
                                    umbrellaCoverage.uninsuredMotoristLimit !== '0')
                              )
                              .map(
                                ({
                                  key,
                                  label,
                                  requirementMet,
                                  blockingDrivers,
                                  onApply,
                                  applyButtonText,
                                  applyTooltip
                                }) => (
                                  <div key={key} className={classes.requirementRow}>
                                    <img
                                      src={requirementMet ? checkCircleIcon : exclamationCircleIcon}
                                      alt={requirementMet ? 'Requirement met' : 'Requirement not met'}
                                      className={classes.requirementRowIcon}
                                    />
                                    <span>
                                      {label}
                                      {!requirementMet && blockingDrivers?.length > 0 && (
                                        <strong>
                                          {' '}
                                          ({blockingDrivers.map((d) => `${d.firstName} ${d.lastName}`).join(', ')})
                                        </strong>
                                      )}
                                    </span>
                                    {!requirementMet && onApply && (
                                      <MUITooltip
                                        title={
                                          applyTooltip ??
                                          'Selecting this will automatically apply this requirement to the policy.'
                                        }
                                        placement="top"
                                        arrow
                                      >
                                        <Button
                                          color="secondary"
                                          variant="text"
                                          className={classes.requirementApplyButton}
                                          onClick={onApply}
                                        >
                                          {applyButtonText ?? 'Apply'}
                                        </Button>
                                      </MUITooltip>
                                    )}
                                  </div>
                                )
                              )}
                          </div>
                        </>
                      )}
                    </div>
                  </NotificationCard>
                )}
              {includeUmbrella && umbrellaCoverage && (
                <>
                  <Grid item xs={12} className={classes.subSection}>
                    <Label type="formSubTitle" className={classes.subSectionTitle}>
                      Coverage
                    </Label>
                  </Grid>
                  <Field
                    id={`${id}.liabilityCoverageLimit`}
                    name={`${id}.liabilityCoverageLimit`}
                    type="select"
                    label="Umbrella liability limit"
                    mode="dark"
                    xs={6}
                    options={umbrellaOpts}
                  />
                  {offerExcessUM[state] ? (
                    <Grid item xs={6} className={classes.fieldWithTooltip}>
                      <FieldWithTooltip
                        id={`${id}.uninsuredMotoristLimit`}
                        name={`${id}.uninsuredMotoristLimit`}
                        type="select"
                        label="UM/Underinsured motorist BI"
                        tooltip={umbrellaTooltips.uninsuredMotoristLimit}
                        showLabelText={false}
                        labelClassName={classes.fieldTooltip}
                        mode="dark"
                        options={validUMUIMOptions}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={6} className={classes.umStateNotice}>
                      <NotificationCard type="infoWhite" noBackground iconSize="icon16">
                        <div className={classes.requirementsContent}>
                          <Label className={classes.requirementsText}>
                            <strong>Please note:</strong> Personal umbrella liability will not include excess UM/UIM in
                            this state.
                          </Label>
                        </div>
                      </NotificationCard>
                    </Grid>
                  )}
                  {canAddUM && validUMUIMOptions.length === 1 && (
                    <Grid item xs={12}>
                      <Label type="smallLightLabel">
                        For additional UM options, a &quot;dev escalation&quot; is required.
                      </Label>
                    </Grid>
                  )}
                  {!isAgency && (
                    <Field
                      id={`${id}.highRisk`}
                      name={`${id}.highRisk`}
                      type="checkbox"
                      label="High risk coverage?"
                      mode="dark"
                      xs={6}
                    />
                  )}
                  <Field
                    id={`${id}.presenceOfBusiness`}
                    name={`${id}.presenceOfBusiness`}
                    type="checkbox"
                    label="Business at home?"
                    mode="dark"
                    xs={6}
                  />
                  <Grid item xs={12} className={classes.subSection}>
                    <Label type="formSubTitle" className={classes.subSectionTitle}>
                      Additional exposures
                    </Label>
                  </Grid>
                  <Grid item xs={6} className={classes.fieldWithTooltip}>
                    <FieldWithTooltip
                      id={`${id}.numLicensedDriversUnder25`}
                      name={`${id}.numLicensedDriversUnder25`}
                      type="select"
                      label="Number of licensed drivers under 25"
                      tooltip={umbrellaTooltips.numLicensedDriversUnder25}
                      showLabelText={false}
                      labelClassName={classes.fieldTooltip}
                      mode="dark"
                      options={selectOptions(0, 10)}
                    />
                  </Grid>
                  <Grid item xs={6} className={classes.fieldWithTooltip}>
                    <FieldWithTooltip
                      id={`${id}.numVehiclesTotal`}
                      name={`${id}.numVehiclesTotal`}
                      type="select"
                      label="Number of cars"
                      tooltip={umbrellaTooltips.numVehiclesTotal}
                      showLabelText={false}
                      labelClassName={classes.fieldTooltip}
                      mode="dark"
                      options={selectOptions(0, 10)}
                    />
                  </Grid>
                  <Field
                    id={`${id}.numMotorcyclesScooters`}
                    name={`${id}.numMotorcyclesScooters`}
                    type="select"
                    label="Number of motorcycles & scooters"
                    mode="dark"
                    xs={6}
                    options={selectOptions(0, 5)}
                  />
                  <Field
                    id={`${id}.numRVs`}
                    name={`${id}.numRVs`}
                    type="select"
                    label="Number of golf carts & snowmobiles"
                    mode="dark"
                    xs={6}
                    options={selectOptions(0, 2)}
                  />
                  <Field
                    id={`${id}.numATVs`}
                    name={`${id}.numATVs`}
                    type="select"
                    label="Number of ATVs"
                    mode="dark"
                    xs={6}
                    options={selectOptions(0, 5)}
                  />
                  <Grid item xs={12} className={classes.subSectionCollapsible}>
                    <CollapsibleSection
                      content={
                        <Label type="formSubTitle" className={classes.subSectionTitle}>
                          Watercrafts
                        </Label>
                      }
                      contentRight={
                        <div className={classes.totalLabel}>
                          <span className={classes.totalText}>TOTAL</span>
                          <span className={classes.totalCount}>
                            {(umbrellaCoverage?.watercraftHullLengths?.filter(Boolean) || []).length +
                              (Number(umbrellaCoverage?.numPersonalWatercraft) || 0)}{' '}
                            Watercrafts
                          </span>
                        </div>
                      }
                    >
                      <Grid container spacing={4}>
                        <Grid item xs={12} className={classes.fieldWithTooltip}>
                          <FieldWithTooltip
                            id={`${id}.numPersonalWatercraft`}
                            name={`${id}.numPersonalWatercraft`}
                            type="select"
                            label="Number of personal watercraft & jet skis"
                            tooltip={umbrellaTooltips.numPersonalWatercraft}
                            showLabelText={false}
                            labelClassName={classes.fieldTooltip}
                            mode="dark"
                            options={selectOptions(0, 5)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <BoatsForm id={id} />
                        </Grid>
                        <Grid item xs={12}>
                          <NotificationCard
                            className={classes.requirementsNotification}
                            iconClassName={classes.requirementsIcon}
                            type="infoWhite"
                            noBackground
                            iconSize="icon16"
                          >
                            <div className={classes.requirementsContent}>
                              <Label className={classes.requirementsText}>
                                <strong>What factors make a watercraft ineligible?</strong> If it&rsquo;s used for
                                purposes other than recreation, if it&rsquo;s capable of speeds exceeding 50 mph, or if
                                it&rsquo;s any of these types: airborne air-powered, amphibious, ducks, homemade,
                                hovercraft, hydro-cycles, hydro-fins, hydroplanes, ice boats, kit boats, kites, land/sea
                                campers, marsh craft, sea mobile, sea scooters, or sea-wing.
                              </Label>
                            </div>
                          </NotificationCard>
                        </Grid>
                      </Grid>
                    </CollapsibleSection>
                  </Grid>
                </>
              )}
            </Grid>
            {includeUmbrella && <PropertiesForm id={id} />}
          </Form>
        </div>
      </Section>
    )
  );
};

Umbrella.propTypes = {
  disabled: PropTypes.bool,
  fromPolicy: PropTypes.bool,
  coverageX: PropTypes.number.isRequired,
  state: PropTypes.string.isRequired
};

Umbrella.defaultProps = {
  disabled: false,
  fromPolicy: false
};

export default observer(Umbrella);
